import { Directive, inject, numberAttribute } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, skip } from 'rxjs';

/**
 * Extend this directive to sync a mat-tab-group's active index with the URL
 * query param `?tab=N`. The tab state survives page reload and browser back/forward.
 *
 * Usage in template:
 *   [selectedIndex]="selectedTabIndex$ | async"
 *   (selectedTabChange)="selectedTabIndex$.next($event.index)"
 */
@Directive()
export abstract class ActiveTabTracker {
  protected readonly selectedTabIndex$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  protected readonly tabIdentifier: string = 'tab';

  protected readonly router: Router = inject(Router);

  protected readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);

  protected constructor() {
    this.selectedTabIndex$
      .pipe(skip(1))
      .subscribe((index: number) => {
        this.router.navigate([], {
          queryParams: { [this.tabIdentifier]: index },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      });

    this._restoreSelectedTab();
  }

  private _restoreSelectedTab(): void {
    const tabIndex: number = numberAttribute(
      this.activatedRoute.snapshot.queryParamMap.get(this.tabIdentifier),
    );
    this.selectedTabIndex$.next(isNaN(tabIndex) ? 0 : tabIndex);
  }
}
